import { css } from "@emotion/react";
import React, { useState, useMemo, useCallback } from "react";
import {
  Button,
  Flex,
  Heading,
  Text,
} from "@phoenix/components";
import {
  TARGET_CATEGORIES,
  buildUnifiedTargetCards,
  calculateCategoryCounts,
  filterTargetCards,
  type TargetCardModel,
  type TargetCategoryId,
  type Target,
} from "./targetCatalog";

// ==========================================
// Crisp SVGs for Providers & Connection Methods
// ==========================================

const ShieldHeaderIcon = () => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0f766e"
    strokeWidth="2.3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="12 8 12 12 15 15" />
  </svg>
);

const SearchIconSVG = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#94a3b8"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const GlobeIconSVG = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2563eb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const OpenAILogo = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="#0f172a"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M21.55 10.004a5.416 5.416 0 00-.478-4.501c-1.217-2.09-3.662-3.166-6.05-2.66A5.59 5.59 0 0010.831 1C8.39.995 6.224 2.546 5.473 4.838A5.553 5.553 0 001.76 7.496a5.487 5.487 0 00.691 6.5 5.416 5.416 0 00.477 4.502c1.217 2.09 3.662 3.165 6.05 2.66A5.586 5.586 0 0013.168 23c2.443.006 4.61-1.546 5.361-3.84a5.553 5.553 0 003.715-2.66 5.488 5.488 0 00-.693-6.497v.001zm-8.381 11.558a4.199 4.199 0 01-2.675-.954c.034-.018.093-.05.132-.074l4.44-2.53a.71.71 0 00.364-.623v-6.176l1.877 1.069c.02.01.033.029.036.05v5.115c-.003 2.274-1.87 4.118-4.174 4.123zM4.192 17.78a4.059 4.059 0 01-.498-2.763c.032.02.09.055.131.078l4.44 2.53c.225.13.504.13.73 0l5.42-3.088v2.138a.068.068 0 01-.027.057L9.9 19.288c-1.999 1.136-4.552.46-5.707-1.51h-.001zM3.023 8.216A4.15 4.15 0 015.198 6.41l-.002.151v5.06a.711.711 0 00.364.624l5.42 3.087-1.876 1.07a.067.067 0 01-.063.005l-4.489-2.559c-1.995-1.14-2.679-3.658-1.53-5.63h.001zm15.417 3.54l-5.42-3.088L14.896 7.6a.067.067 0 01.063-.006l4.489 2.557c1.998 1.14 2.683 3.662 1.529 5.633a4.163 4.163 0 01-2.174 1.807V12.38a.71.71 0 00-.363-.623zm1.867-2.773a6.04 6.04 0 00-.132-.078l-4.44-2.53a.731.731 0 00-.729 0l-5.42 3.088V7.325a.068.068 0 01.027-.057L14.1 4.713c2-1.137 4.555-.46 5.707 1.513.487.833.664 1.809.499 2.757h.001zm-11.741 3.81l-1.877-1.068a.065.065 0 01-.036-.051V6.559c.001-2.277 1.873-4.122 4.181-4.12.976 0 1.92.338 2.671.954-.034.018-.092.05-.131.073l-4.44 2.53a.71.71 0 00-.365.623l-.003 6.173v.002zm1.02-2.168L12 9.25l2.414 1.375v2.75L12 14.75l-2.415-1.375v-2.75z" />
  </svg>
);

const AnthropicLogo = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="#0f172a"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M13.827 3.52h3.603L24 20h-3.603l-6.57-16.48zm-7.258 0h3.767L16.906 20h-3.674l-1.343-3.461H5.017l-1.344 3.46H0L6.57 3.522zm4.132 9.959L8.453 7.687 6.205 13.48H10.7z" />
  </svg>
);

const GoogleLogo = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
      fill="#EA4335"
    />
  </svg>
);

const AzureLogo = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M11.49 2H2v9.492h9.492V2h-.002z" fill="#F25022" />
    <path d="M22 2h-9.492v9.492H22V2z" fill="#7FBA00" />
    <path d="M11.49 12.508H2V22h9.492v-9.492h-.002z" fill="#00A4EF" />
    <path d="M22 12.508h-9.492V22H22v-9.492z" fill="#FFB900" />
  </svg>
);

const OllamaLogo = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="#0f172a"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M7.905 1.09c.216.085.411.225.588.41.295.306.544.744.734 1.263.191.522.315 1.1.362 1.68a5.054 5.054 0 012.049-.636l.051-.004c.87-.07 1.73.087 2.48.474.101.053.2.11.297.17.05-.569.172-1.134.36-1.644.19-.52.439-.957.733-1.264a1.67 1.67 0 01.589-.41c.257-.1.53-.118.796-.042.401.114.745.368 1.016.737.248.337.434.769.561 1.287.23.934.27 2.163.115 3.645l.053.04.026.019c.757.576 1.284 1.397 1.563 2.35.435 1.487.216 3.155-.534 4.088l-.018.021.002.003c.417.762.67 1.567.724 2.4l.002.03c.064 1.065-.2 2.137-.814 3.19l-.007.01.01.024c.472 1.157.62 2.322.438 3.486l-.006.039a.651.651 0 01-.747.536.648.648 0 01-.54-.742c.167-1.033.01-2.069-.48-3.123a.643.643 0 01.04-.617l.004-.006c.604-.924.854-1.83.8-2.72-.046-.779-.325-1.544-.8-2.273a.644.644 0 01.18-.886l.009-.006c.243-.159.467-.565.58-1.12a4.229 4.229 0 00-.095-1.974c-.205-.7-.58-1.284-1.105-1.683-.595-.454-1.383-.673-2.38-.61a.653.653 0 01-.632-.371c-.314-.665-.772-1.141-1.343-1.436a3.288 3.288 0 00-1.772-.332c-1.245.099-2.343.801-2.67 1.686a.652.652 0 01-.61.425c-1.067.002-1.893.252-2.497.703-.522.39-.878.935-1.066 1.588a4.07 4.07 0 00-.068 1.886c.112.558.331 1.02.582 1.269l.008.007c.212.207.257.53.109.785-.36.622-.629 1.549-.673 2.44-.05 1.018.186 1.902.719 2.536l.016.019a.643.643 0 01.095.69c-.576 1.236-.753 2.252-.562 3.052a.652.652 0 01-1.269.298c-.243-1.018-.078-2.184.473-3.498l.014-.035-.008-.012a4.339 4.339 0 01-.598-1.309l-.005-.019a5.764 5.764 0 01-.177-1.785c.044-.91.278-1.842.622-2.59l.012-.026-.002-.002c-.293-.418-.51-.953-.63-1.545l-.005-.024a5.352 5.352 0 01.093-2.49c.262-.915.777-1.701 1.536-2.269.06-.045.123-.09.186-.132-.159-1.493-.119-2.73.112-3.67.127-.518.314-.95.562-1.287.27-.368.614-.622 1.015-.737.266-.076.54-.059.797.042z" />
  </svg>
);

const LangChainLogo = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2563eb"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const WebSocketLogo = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0284c7"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12.55a11 11 0 0 1 14.08 0" />
    <path d="M1.42 9a16 16 0 0 1 21.16 0" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <circle cx="12" cy="20" r="1.5" fill="#0284c7" />
  </svg>
);

const VLLMLogo = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 4l7.5 16L15 11l-4.5-7H3z"
      fill="#2563eb"
    />
    <path
      d="M13.5 11l3 9 4.5-16h-4.5l-3 7z"
      fill="#eab308"
    />
  </svg>
);

const LMStudioLogo = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="3" y="3" width="18" height="18" rx="5" fill="#7c3aed" />
    <circle cx="9" cy="9" r="2" fill="#ffffff" />
    <circle cx="15" cy="9" r="2" fill="#ffffff" />
    <path d="M8 15h8" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const AppLogo = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0f766e"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const TracesLogo = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0f766e"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 3v18h18" />
    <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" />
  </svg>
);

const PythonLogo = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2c-3.5 0-5 1.5-5 3.5v2h5v1.5H5.5C3.5 9 2 10.5 2 14c0 3.5 1.5 5 3.5 5h2v-2.5c0-1.5 1-2.5 2.5-2.5h5c1.5 0 2.5-1 2.5-2.5V7c0-2-1.5-5-5.5-5zm-2 2a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"
      fill="#3b82f6"
    />
    <path
      d="M12 22c3.5 0 5-1.5 5-3.5v-2h-5v-1.5h6.5c2 0 3.5-1.5 3.5-5 0-3.5-1.5-5-3.5-5h-2v2.5c0 1.5-1 2.5-2.5 2.5h-5c-1.5 0-2.5 1-2.5 2.5V17c0 2 1.5 5 5.5 5zm2-2a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"
      fill="#eab308"
    />
  </svg>
);

const JavaScriptLogo = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect x="2" y="2" width="20" height="20" rx="4" fill="#facc15" />
    <path
      d="M12 17.5c.5.8 1.4 1.3 2.5 1.3 1.5 0 2.5-.8 2.5-2 0-1.3-.8-1.8-2.2-2.4l-.8-.3c-2.3-1-3.3-2.2-3.3-4.2 0-2.4 1.8-4.1 4.5-4.1 1.8 0 3.1.6 4 2.1l-2 1.3c-.5-.9-1.2-1.3-2-1.3-1.2 0-2 .8-2 1.7 0 1.1.7 1.6 2 2.1l.8.3c2.6 1.1 3.7 2.3 3.7 4.5 0 2.6-2 4.3-4.9 4.3-2.3 0-3.9-.9-4.8-2.6l2.1-1.3z"
      fill="#0f172a"
    />
  </svg>
);

const BrowserLogo = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#6366f1"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="2" y1="8" x2="22" y2="8" />
    <circle cx="5" cy="5.5" r="0.75" fill="#6366f1" />
    <circle cx="8" cy="5.5" r="0.75" fill="#6366f1" />
    <circle cx="11" cy="5.5" r="0.75" fill="#6366f1" />
    <path d="M6 21l3-4h6l3 4" />
  </svg>
);

const GoLogo = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect x="2" y="2" width="20" height="20" rx="5" fill="#00add8" />
    <path
      d="M7 12a3.5 3.5 0 1 1 3.5 3.5H9"
      stroke="#ffffff"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M15 12a3 3 0 1 1-3-3"
      stroke="#ffffff"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ShellLogo = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#059669"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="3" width="20" height="18" rx="3" />
    <path d="M7 9l4 3-4 3" />
    <line x1="13" y1="15" x2="17" y2="15" />
  </svg>
);

const CustomTargetLogo = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0d9488"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const AgentLogo = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#7c3aed"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <circle cx="12" cy="5" r="2" />
    <path d="M12 7v4" />
    <line x1="8" y1="16" x2="8" y2="16" />
    <line x1="16" y1="16" x2="16" y2="16" />
  </svg>
);

const AWSLogo = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect x="2" y="2" width="20" height="20" rx="4" fill="#232f3e" />
    <path
      d="M6 14.5c3.5 2.5 8.5 2.5 12 0"
      stroke="#ff9900"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M16.5 13.5l1.8 1-1.2 1.5"
      stroke="#ff9900"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const CodeLogo = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0284c7"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

function renderTargetIcon(iconType: string): React.ReactNode {
  switch (iconType) {
    case "openai":
      return <OpenAILogo />;
    case "anthropic":
      return <AnthropicLogo />;
    case "google":
      return <GoogleLogo />;
    case "azure":
      return <AzureLogo />;
    case "ollama":
      return <OllamaLogo />;
    case "langchain":
      return <LangChainLogo />;
    case "websocket":
      return <WebSocketLogo />;
    case "vllm":
      return <VLLMLogo />;
    case "lmstudio":
      return <LMStudioLogo />;
    case "traces":
      return <TracesLogo />;
    case "http":
      return <GlobeIconSVG />;
    case "python":
      return <PythonLogo />;
    case "javascript":
      return <JavaScriptLogo />;
    case "browser":
      return <BrowserLogo />;
    case "go":
      return <GoLogo />;
    case "shell":
      return <ShellLogo />;
    case "custom":
      return <CustomTargetLogo />;
    case "agent":
      return <AgentLogo />;
    case "aws":
      return <AWSLogo />;
    case "code":
      return <CodeLogo />;
    case "app":
    default:
      return <AppLogo />;
  }
}

// ==========================================
// Emotion Styles Matching Evalight Design System
// ==========================================

const targetSelectionCard = css`
  background: var(--global-color-surface-lowest, #ffffff);
  border: 1px solid var(--global-border-color-default, #e2e8f0);
  border-radius: 16px;
  padding: 24px 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .theme--dark & {
    background: #1e293b !important;
    border-color: #334155 !important;
  }
`;

const headerRow = css`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
`;

const shieldIconBox = css`
  background: #f0fdfa;
  border: 1.5px solid #99f6e4;
  width: 50px;
  height: 50px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .theme--dark & {
    background: rgba(15, 118, 110, 0.2) !important;
    border-color: rgba(20, 184, 166, 0.4) !important;
  }
`;

// Horizontal Stepper Bar
const stepperContainer = css`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 14px;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 22px;

  .theme--dark & {
    background: #0f172a !important;
    border-color: #334155 !important;
  }
`;

const stepperStep = (isActive: boolean, isDone: boolean) => css`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: ${isActive ? 750 : 500};
  color: ${isActive ? "#0f766e" : isDone ? "#0f172a" : "#64748b"};
  cursor: default;

  .theme--dark & {
    color: ${isActive ? "#2dd4bf" : isDone ? "#f8fafc" : "#94a3b8"} !important;
  }
`;

const stepNumberBadge = (isActive: boolean, isDone: boolean) => css`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  background: ${isActive ? "#0f766e" : isDone ? "#ccfbf1" : "transparent"};
  color: ${isActive ? "#ffffff" : isDone ? "#0f766e" : "#64748b"};
  border: 1.5px solid ${isActive ? "#0f766e" : isDone ? "#14b8a6" : "#cbd5e1"};

  .theme--dark & {
    background: ${isActive ? "#0f766e" : isDone ? "rgba(15,118,110,0.3)" : "transparent"} !important;
    color: ${isActive ? "#ffffff" : isDone ? "#2dd4bf" : "#94a3b8"} !important;
  }
`;

const stepperDivider = css`
  color: #cbd5e1;
  font-size: 0.8rem;
`;

// Search Bar
const searchContainer = css`
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 18px;
  width: 100%;
`;

const searchInputWrapper = css`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

const searchIconWrapper = css`
  position: absolute;
  left: 14px;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const searchInputElement = css`
  width: 100%;
  height: 42px;
  padding: 0 38px 0 40px;
  background: #ffffff;
  border: 1.5px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.92rem;
  font-weight: 550;
  color: #0f172a;
  outline: none;
  transition: all 0.2s ease-in-out;

  &:focus {
    border-color: #0f766e;
    box-shadow: 0 0 0 3px rgba(15, 118, 110, 0.12);
  }

  &::placeholder {
    color: #94a3b8;
    font-weight: 450;
  }

  .theme--dark & {
    background: #0f172a !important;
    border-color: #334155 !important;
    color: #ffffff !important;
  }
`;

const searchClearBtn = css`
  position: absolute;
  right: 12px;
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;

  &:hover {
    color: #475569;
  }
`;

// Category Filter Tabs
const categoryTabsRow = css`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 22px;
`;

const categoryTabBtn = (isSelected: boolean) => css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 9999px;
  font-size: 0.85rem;
  font-weight: ${isSelected ? 700 : 550};
  cursor: pointer;
  transition: all 0.18s ease-in-out;
  border: 1.5px solid ${isSelected ? "#0f766e" : "#e2e8f0"};
  background: ${isSelected ? "#0f766e" : "#ffffff"};
  color: ${isSelected ? "#ffffff" : "#475569"};

  &:hover {
    border-color: ${isSelected ? "#0f766e" : "#cbd5e1"};
    background: ${isSelected ? "#0f766e" : "#f8fafc"};
    color: ${isSelected ? "#ffffff" : "#0f172a"};
  }

  &:focus-visible {
    outline: 2px solid #0f766e;
    outline-offset: 2px;
  }

  .theme--dark & {
    background: ${isSelected ? "#0f766e" : "#1e293b"} !important;
    border-color: ${isSelected ? "#0f766e" : "#334155"} !important;
    color: ${isSelected ? "#ffffff" : "#cbd5e1"} !important;
  }
`;

const categoryCountBadge = (isSelected: boolean) => css`
  font-size: 0.76rem;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 9999px;
  background: ${isSelected ? "rgba(255, 255, 255, 0.25)" : "#f1f5f9"};
  color: ${isSelected ? "#ffffff" : "#64748b"};

  .theme--dark & {
    background: ${isSelected ? "rgba(255, 255, 255, 0.25)" : "#334155"} !important;
    color: ${isSelected ? "#ffffff" : "#94a3b8"} !important;
  }
`;

// Responsive 3-Column Card Grid
const targetCardsGrid = css`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

// Target Card
const targetCard = (isSelected: boolean) => css`
  background: ${isSelected ? "#f0fdfa" : "#ffffff"};
  border: 2px solid ${isSelected ? "#0f766e" : "#e2e8f0"};
  border-radius: 12px;
  padding: 18px 20px;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s ease-in-out;
  box-shadow: ${isSelected ? "0 4px 14px rgba(15, 118, 110, 0.09)" : "0 1px 3px rgba(0, 0, 0, 0.02)"};
  outline: none;

  &:hover {
    border-color: ${isSelected ? "#0f766e" : "#0d9488"};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(15, 118, 110, 0.07);
  }

  &:focus-visible {
    outline: 2px solid #0f766e;
    outline-offset: 2px;
  }

  .theme--dark & {
    background: ${isSelected ? "rgba(15, 118, 110, 0.15)" : "#0f172a"} !important;
    border-color: ${isSelected ? "#0f766e" : "#334155"} !important;
  }
`;

const iconBox = (isSelected: boolean) => css`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${isSelected ? "#ccfbf1" : "#f8fafc"};
  border: 1px solid ${isSelected ? "#99f6e4" : "#e2e8f0"};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  .theme--dark & {
    background: ${isSelected ? "rgba(15, 118, 110, 0.3)" : "#1e293b"} !important;
    border-color: ${isSelected ? "#0f766e" : "#334155"} !important;
  }
`;

const cardBody = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const titleBadgeRow = css`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const cardTitle = css`
  font-weight: 750;
  font-size: 0.95rem;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .theme--dark & {
    color: #ffffff !important;
  }
`;

const cardDescription = css`
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  .theme--dark & {
    color: #94a3b8 !important;
  }
`;

const cardBadge = (type?: string) => {
  let bg = "#f1f5f9";
  let color = "#475569";

  if (type === "Popular") {
    bg = "#e0f2fe";
    color = "#0369a1";
  } else if (type === "Agent") {
    bg = "#ede9fe";
    color = "#6d28d9";
  } else if (type === "Local") {
    bg = "#f1f5f9";
    color = "#475569";
  } else if (type === "My Application") {
    bg = "#dcfce7";
    color = "#15803d";
  }

  return css`
    font-size: 0.72rem;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 9999px;
    background: ${bg};
    color: ${color};
    text-transform: capitalize;
    letter-spacing: 0.02em;
    display: inline-block;
  `;
};

// Radio selection circle
const radioCircle = (isSelected: boolean) => css`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${isSelected ? "#0f766e" : "#cbd5e1"};
  background: ${isSelected ? "#0f766e" : "#ffffff"};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.18s ease-in-out;

  &::after {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #ffffff;
    opacity: ${isSelected ? 1 : 0};
    transform: scale(${isSelected ? 1 : 0.5});
    transition: all 0.18s ease-in-out;
  }

  .theme--dark & {
    background: ${isSelected ? "#0f766e" : "#1e293b"} !important;
  }
`;

// Bottom Action Bar
const bottomActionBar = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #e2e8f0;
  padding-top: 20px;
  margin-top: 8px;

  .theme--dark & {
    border-color: #334155 !important;
  }
`;

// Empty Search Result
const emptySearchCard = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  border: 1.5px dashed #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
  text-align: center;
  margin-bottom: 24px;
`;

export interface TargetTypeSelectionProps {
  savedTargets: Target[];
  selectedCardId: string;
  onSelectCard: (card: TargetCardModel) => void;
  onContinue: (card: TargetCardModel) => void;
  onCancel?: () => void;
  onConfigureNewApp?: () => void;
  hideBottomBar?: boolean;
}

export function TargetTypeSelection({
  savedTargets: _savedTargets,
  selectedCardId,
  onSelectCard,
  onContinue,
  onCancel,
  onConfigureNewApp,
  hideBottomBar = false,
}: TargetTypeSelectionProps) {
  const [activeCategory, setActiveCategory] = useState<TargetCategoryId>("all");
  const [searchQuery, setSearchQuery] = useState("");

  // 1. Build unified cards from 55 target catalog definitions
  // Executed applications are not listed in target type selection cards per user requirement
  const allCards = useMemo(() => {
    return buildUnifiedTargetCards();
  }, []);

  // 2. Compute dynamic counts from actual catalog categories (never hardcoded)
  const categoryCounts = useMemo(() => {
    return calculateCategoryCounts();
  }, []);

  // 3. Filter cards by active category and search keyword
  const visibleCards = useMemo(() => {
    return filterTargetCards(allCards, activeCategory, searchQuery);
  }, [allCards, activeCategory, searchQuery]);

  // Find currently selected card model
  const activeSelectedCard = useMemo(() => {
    return allCards.find((c) => c.id === selectedCardId) || allCards[0] || null;
  }, [allCards, selectedCardId]);

  const handleCardClick = useCallback(
    (card: TargetCardModel) => {
      onSelectCard(card);
    },
    [onSelectCard]
  );

  const handleCardKeyDown = useCallback(
    (e: React.KeyboardEvent, card: TargetCardModel) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelectCard(card);
      }
    },
    [onSelectCard]
  );

  const handleContinueClick = useCallback(() => {
    if (activeSelectedCard) {
      onContinue(activeSelectedCard);
    }
  }, [activeSelectedCard, onContinue]);

  return (
    <div css={targetSelectionCard}>
      {/* 1. Page Header */}
      <div css={headerRow}>
        <div css={shieldIconBox}>
          <ShieldHeaderIcon />
        </div>
        <Flex direction="column" gap="size-25">
          <Heading
            level={1}
            style={{
              margin: 0,
              fontSize: "1.55rem",
              fontWeight: 800,
              color: "#0f172a",
            }}
          >
            Target Configuration
          </Heading>
          <Text style={{ color: "#64748b", fontSize: "0.9rem" }}>
            Select how you want to run security tests on your AI system.
          </Text>
        </Flex>
      </div>

      {/* 2. Wizard Stepper Bar */}
      <div css={stepperContainer}>
        <div css={stepperStep(true, false)}>
          <div css={stepNumberBadge(true, false)}>1</div>
          <span>Target type</span>
        </div>
        <span css={stepperDivider}>&rarr;</span>
        <div css={stepperStep(false, false)}>
          <div css={stepNumberBadge(false, false)}>2</div>
          <span>Target config</span>
        </div>
        <span css={stepperDivider}>&rarr;</span>
        <div css={stepperStep(false, false)}>
          <div css={stepNumberBadge(false, false)}>3</div>
          <span>Application details</span>
        </div>
        <span css={stepperDivider}>&rarr;</span>
        <div css={stepperStep(false, false)}>
          <div css={stepNumberBadge(false, false)}>4</div>
          <span>Model config</span>
        </div>
        <span css={stepperDivider}>&rarr;</span>
        <div css={stepperStep(false, false)}>
          <div css={stepNumberBadge(false, false)}>5</div>
          <span>Plugins</span>
        </div>
        <span css={stepperDivider}>&rarr;</span>
        <div css={stepperStep(false, false)}>
          <div css={stepNumberBadge(false, false)}>6</div>
          <span>Strategies</span>
        </div>
        <span css={stepperDivider}>&rarr;</span>
        <div css={stepperStep(false, false)}>
          <div css={stepNumberBadge(false, false)}>7</div>
          <span>Review</span>
        </div>
      </div>

      {/* 3. Search Bar */}
      <div css={searchContainer}>
        <div css={searchInputWrapper}>
          <div css={searchIconWrapper}>
            <SearchIconSVG />
          </div>
          <input
            type="text"
            css={searchInputElement}
            placeholder="Search providers or targets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              type="button"
              css={searchClearBtn}
              onClick={() => setSearchQuery("")}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* 4. Target Category Filter Chips with Dynamic Counts */}
      <div css={categoryTabsRow}>
        {TARGET_CATEGORIES.map((cat) => {
          const isSelected = activeCategory === cat.id;
          const count = categoryCounts[cat.id] ?? 0;
          return (
            <button
              key={cat.id}
              type="button"
              css={categoryTabBtn(isSelected)}
              onClick={() => setActiveCategory(cat.id)}
            >
              <span>{cat.label}</span>
              <span css={categoryCountBadge(isSelected)}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* 5. Responsive Grid of Target Cards */}
      {visibleCards.length > 0 ? (
        <div css={targetCardsGrid} role="radiogroup" aria-label="Target integrations">
          {visibleCards.map((card) => {
            const isSelected = activeSelectedCard?.id === card.id;

            return (
              <div
                key={card.id}
                css={targetCard(isSelected)}
                onClick={() => handleCardClick(card)}
                onKeyDown={(e) => handleCardKeyDown(e, card)}
                tabIndex={0}
                role="radio"
                aria-checked={isSelected}
              >
                {/* Icon */}
                <div css={iconBox(isSelected)}>
                  {renderTargetIcon(card.iconType)}
                </div>

                {/* Content */}
                <div css={cardBody}>
                  <div css={titleBadgeRow}>
                    <span css={cardTitle} title={card.name}>
                      {card.name}
                    </span>
                    {card.badge && (
                      <span css={cardBadge(card.badge)}>{card.badge}</span>
                    )}
                  </div>
                  <span css={cardDescription} title={card.description}>
                    {card.description}
                  </span>
                </div>

                {/* Radio Circle */}
                <div css={radioCircle(isSelected)} />
              </div>
            );
          })}
        </div>
      ) : (
        /* Empty State */
        <div css={emptySearchCard}>
          <Heading level={3} style={{ fontSize: "1.1rem", color: "#0f172a", marginBottom: "6px" }}>
            {activeCategory === "my-applications" && !searchQuery
              ? "No Saved Applications Yet"
              : "No Matching Targets"}
          </Heading>
          <Text style={{ color: "#64748b", fontSize: "0.88rem", maxWidth: "420px", marginBottom: "16px" }}>
            {activeCategory === "my-applications" && !searchQuery
              ? "You haven't configured any custom applications yet. Configure an HTTP or WebSocket target to save it to your library."
              : `No targets found matching "${searchQuery}" in ${
                  TARGET_CATEGORIES.find((c) => c.id === activeCategory)?.label || "the catalog"
                }.`}
          </Text>
          {activeCategory === "my-applications" && onConfigureNewApp ? (
            <Button
              type="button"
              style={{
                background: "#0f766e",
                color: "#ffffff",
                borderRadius: "8px",
                padding: "8px 18px",
                fontWeight: 700,
              }}
              onClick={onConfigureNewApp}
            >
              + Configure New Application
            </Button>
          ) : (
            <Button
              variant="default"
              type="button"
              style={{
                borderRadius: "8px",
                padding: "8px 16px",
                fontWeight: 650,
              }}
              onClick={() => {
                setSearchQuery("");
                setActiveCategory("all");
              }}
            >
              Reset Filters
            </Button>
          )}
        </div>
      )}

      {/* 6. Bottom Navigation Bar */}
      {!hideBottomBar && (
        <div css={bottomActionBar}>
          {onCancel && (
            <Button
              variant="default"
              type="button"
              style={{
                padding: "9px 20px",
                fontSize: "0.92rem",
                fontWeight: 650,
                borderRadius: "8px",
                border: "1.5px solid #cbd5e1",
                color: "#475569",
                background: "#ffffff",
              }}
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
          <Button
            type="button"
            isDisabled={!activeSelectedCard}
            style={{
              padding: "9px 24px",
              fontSize: "0.95rem",
              fontWeight: 750,
              borderRadius: "8px",
              background: "#0f766e",
              color: "#ffffff",
              border: "1px solid #115e59",
              cursor: activeSelectedCard ? "pointer" : "not-allowed",
            }}
            onClick={handleContinueClick}
          >
            Next &rarr;
          </Button>
        </div>
      )}
    </div>
  );
}
